import { formatShare, formatUsd } from '../time';
import { VENDOR_COLORS, VENDOR_LABELS, type VendorCost } from '../vendors';

interface VendorBreakdownProps {
  vendors: VendorCost[];
  /** Total the share column is computed against. */
  total: number;
  /** Shown in place of the rows when there is nothing to attribute. */
  emptyLabel?: string;
  /** Nested under a parent row (indented, smaller) rather than a standalone panel. */
  nested?: boolean;
}

/**
 * Vendor rows for a cost total: who we pay, how many calls, how much, and what
 * share. Used nested under a service in "By service" and as the standalone
 * panel on Merch Cost, so both read identically.
 */
export function VendorBreakdown({ vendors, total, emptyLabel, nested = false }: VendorBreakdownProps) {
  if (vendors.length === 0) {
    return (
      <p className={`text-xs text-slate-400 ${nested ? 'py-1.5 pl-4' : 'py-2'}`}>
        {emptyLabel ?? 'No vendor attribution available for this period.'}
      </p>
    );
  }

  const max = vendors.reduce((acc, v) => Math.max(acc, v.cost), 0);

  return (
    <div className={nested ? 'pl-4 pb-1.5' : ''}>
      <div
        className={`hidden sm:grid grid-cols-[1fr_repeat(3,minmax(0,7rem))] gap-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 ${
          nested ? '' : 'border-b border-slate-100 mb-1'
        }`}
      >
        <span>Vendor</span>
        <span className="text-right">Calls</span>
        <span className="text-right">Cost</span>
        <span className="text-right">Share</span>
      </div>
      {vendors.map((v) => (
        <div
          key={v.vendor}
          className="grid grid-cols-2 sm:grid-cols-[1fr_repeat(3,minmax(0,7rem))] gap-2 py-1.5 text-xs items-center"
          title={v.detail.length ? v.detail.join('\n') : undefined}
        >
          <span className="col-span-2 sm:col-span-1 flex items-center gap-2 min-w-0">
            <span
              className="inline-block w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: VENDOR_COLORS[v.vendor] }}
            />
            <span className="text-slate-700 whitespace-nowrap">{VENDOR_LABELS[v.vendor]}</span>
            {/* Relative-cost bar: reads at a glance without a chart. It gives up
                its width before the vendor label does, so the label is never
                cut to an ellipsis in the narrow nested columns. */}
            <span className="hidden sm:block flex-1 min-w-0 max-w-[3.5rem] h-1 rounded-full bg-slate-100">
              <span
                className="block h-1 rounded-full"
                style={{
                  width: max > 0 ? `${Math.max(2, (v.cost / max) * 100)}%` : '0%',
                  backgroundColor: VENDOR_COLORS[v.vendor],
                  opacity: 0.45,
                }}
              />
            </span>
          </span>
          <span className="text-right tabular-nums text-slate-500" data-label="Calls">
            <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Calls</span>
            {v.calls || '—'}
          </span>
          <span className="text-right tabular-nums text-slate-700" data-label="Cost">
            <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost</span>
            {formatUsd(v.cost)}
          </span>
          <span className="text-right tabular-nums text-slate-500" data-label="Share">
            <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Share</span>
            {formatShare(v.cost, total)}
          </span>
        </div>
      ))}
    </div>
  );
}
